@let isEditMode = (pageViewAndEditModeSwitchManager.mode | async) === 'edit';
@let showGridGuideLayout = (gridItems$ | async)?.length && isEditMode;
@let authorizedDashboards = authorizedDashboardIds();

@if (dialogData) {
  <h1 mat-dialog-title>{{ dialogData.dashboard.name }}</h1>
}

<div class="px-2 pt-2" [class.content-in-dialog]="dialogData">
  @if (!(gridItems$ | async)?.length) {
    <h4 class="mt-5 text-center empty-message">
      <img class="dashboard-logo" src="assets/images/dashboard.svg" alt="Custom Dashboard" />
      <div class="mt-4 text-body-large">{{ 'page.widgetContent.noWidget.title' | translate }}</div>
      <p class="text-grey text-body-small">
        {{ 'page.widgetContent.noWidget.description' | translate }}
      </p>
      @if (!pageViewAndEditModeSwitchManager.isLockedInView() && !dialogData) {
        <button mat-flat-button class="mt-3" (click)="switchToEditModeAndOpenAddModal()">
          <mat-icon>dashboard_customize</mat-icon>
          {{ 'page.widgetContent.addWidget' | translate }}
        </button>
      }
    </h4>
  }

  <div
    class="grid-stack-area has-screen-limit"
    [class.grid-guide-overlay]="showGridGuideLayout"
    [class.has-screen-limit]="showGridGuideLayout && dashboardLayout === EDashboardLayout.fitToScreen"
  >
    <gridstack #gridstack [options]="gridOptions">
      @for (widget of gridItems$ | async; track widget) {
        @let isExceptionalCaseMessageShown =
          (widget.widgetInputs.exceptionalCaseInformationSubject | async)?.length &&
          (widget.widgetInputs.isDummyMode | async) === false;

        <gridstack-item
          #gridStackItem
          [options]="widget.gridstackOptions"
          [class]="{
            'edit-mode': isEditMode,
            'widget-failure': (widget.widgetInputs.dataLoadStatusSubject | async) === ELoadStatus.failure,
            'widget-highlighted': widget.isHighlighted(),
            'highlight-active': isAnyFilterHovered(),
          }"
          infiniteAutoScroll
          [scrollDelay]="100"
          [stopAutoScroll]="
            !widget.widgetData.configuration.isAutoScrollEnabled ||
            ((widget.widgetInputs.exceptionalCaseInformationSubject | async)?.length ?? 0) > 0 ||
            isEditMode ||
            isAnyFilterHovered()
          "
        >
          <div class="widget-container">
            <div class="widget-header">
              <div class="configuration" [ngClass]="{ 'edit-mode': isEditMode }">
                <mat-icon
                  *ngIf="isEditMode"
                  [matTooltip]="'page.widgetContent.addEditDialog.editTitle' | translate"
                  (click)="onClickedWidgetEdit(widget)"
                  mat-ripple
                  >edit
                </mat-icon>
                <mat-icon
                  *ngIf="isEditMode"
                  [matTooltip]="'page.widgetContent.cloneConfirmation.title' | translate"
                  (click)="onClickedWidgetClone(widget.widgetData)"
                  mat-ripple
                  >content_copy
                </mat-icon>
                <mat-icon
                  *ngIf="isEditMode"
                  [matTooltip]="'page.widgetContent.deleteDialog.title' | translate"
                  (click)="onClickedWidgetRemove(widget)"
                  mat-ripple
                  >delete
                </mat-icon>

                @if (!isEditMode && widget.widgetData.subDashboardId) {
                  @let isUnauthorized =
                    authorizedDashboards !== -1 &&
                    !(
                      authorizedDashboards
                      | arrayIncludes: widget.widgetData.subDashboardId : authorizedDashboards.length
                    );

                  <mat-icon
                    [class.disabled-icon]="isUnauthorized"
                    [matTooltip]="
                      (isUnauthorized
                        ? 'page.widgetContent.subDashboard.unauthorized'
                        : 'page.widgetContent.subDashboard.open'
                      ) | translate
                    "
                    (click)="
                      openSubDashboard(
                        widget.widgetData.subDashboardId,
                        widget.widgetData.configuration.subDashboardWindowSize
                      )
                    "
                    mat-ripple
                    >launch
                  </mat-icon>
                }

                <mat-icon
                  *ngIf="
                    !isEditMode &&
                    widget.widgetData.type === 'formIo' &&
                    !(widget.widgetInputs.exceptionalCaseInformationSubject | async)?.length
                  "
                  [matTooltip]="'widget.formIo.iconButtons.history' | translate"
                  (click)="showWidgetSubmissions(widget)"
                  mat-ripple
                  >history
                </mat-icon>
                <mat-icon
                  *ngIf="
                    !isEditMode &&
                    widget.widgetData.configuration.actionTracker &&
                    isActionTrackerModuleAllowed() &&
                    (widget.widgetData.configuration.actionTracker.boardId ||
                      widget.widgetData.configuration.actionTracker.itemCategoryId ||
                      widget.widgetData.configuration.actionTracker.itemTypeId)
                  "
                  class="material-icons-outlined"
                  [matTooltip]="'button.addAction' | translate"
                  (click)="onClickedAddAction(widget)"
                  mat-ripple
                  >note_add
                </mat-icon>

                @if (
                  !isEditMode && widget.widgetData.configuration.openDfPageConfiguration && isOeeTrackerModuleAllowed()
                ) {
                  <mat-icon
                    class="material-icons-outlined"
                    [matTooltip]="'button.openDfPage' | translate"
                    (click)="openDfPage(widget)"
                    mat-ripple
                    >open_in_browser
                  </mat-icon>
                }

                @for (action of widget.headerActions; track action.id) {
                  @if (!isEditMode && action && action.isVisible) {
                    <mat-icon
                      [ngClass]="action.class ? action.class : ''"
                      [matTooltip]="'widget.' + widget.widgetData.type + '.iconButtons.' + action.id | translate"
                      (click)="clickHeaderAction(widget, action.id)"
                      mat-ripple
                    >
                      {{ action.icon }}
                    </mat-icon>
                  }
                }
              </div>

              @if (!isEditMode && !isWidgetFilterButtonHidden() && widget.doesWidgetUseAnyFilter()) {
                <div class="widget-filter-container">
                  <mat-icon class="widget-filter material-icons-outlined" (click)="openWidgetFilterDialog(widget)"
                    >filter_alt
                  </mat-icon>
                </div>
              }

              @if (widget.widgetData.title || widget.widgetData.subtitle) {
                <div class="widget-titles">
                  @if (widget.widgetData.title) {
                    <h1 class="title text-title-large">{{ widget.widgetData.title }}</h1>
                  }
                  @if (widget.widgetData.subtitle) {
                    <span class="subtitle text-grey text-title-medium">{{ widget.widgetData.subtitle }}</span>
                  }
                </div>
              }

              @if (
                (widget.widgetInputs.dataLoadStatusSubject | async) === ELoadStatus.loading &&
                !(widget.widgetInputs.exceptionalCaseInformationSubject | async)?.length
              ) {
                <mat-progress-bar class="position-absolute" mode="indeterminate" color="primary"></mat-progress-bar>
              }
            </div>
            @let isNoteQuickEditable =
              widget.widgetData.type === 'note' && !isEditMode && !pageViewAndEditModeSwitchManager.isLockedInView();

            <div
              class="widget-content"
              [class]="{
                'blur-effect': isExceptionalCaseMessageShown,
                'note-quick-editable': isNoteQuickEditable,
              }"
              [style.zoom]="widget.widgetData.configuration.zoom / 100"
              (click)="onNoteWidgetClicked(widget, isEditMode)"
            >
              <app-widget-wrapper [component]="widget.component" [injector]="widget.injector"></app-widget-wrapper>
            </div>

            @if (isExceptionalCaseMessageShown) {
              <widget-exceptional-case-information
                [data]="(widget.widgetInputs.exceptionalCaseInformationSubject | async) ?? []"
              ></widget-exceptional-case-information>
            }

            @if (
              widget.widgetData.configuration.isLastUpdatedLabelShown &&
              !isEditMode &&
              (widget.widgetInputs.lastDataLoadDateSubject | async)
            ) {
              <div class="last-update-info">
                <i class="b-fa-solid b-fa-rotate"></i>
                {{ widget.widgetInputs.lastDataLoadDateSubject | async }}
              </div>
            }
          </div>
        </gridstack-item>
      }
    </gridstack>
  </div>

  @if (!dialogData?.subDashboardId) {
    <div [hidden]="isEditMode" class="dashboard-scw-logo" role="img" aria-label="SCW.AI Logo"></div>
  }
</div>

@if (this.dialogData) {
  <mat-dialog-actions align="end">
    <button matButton="tonal" mat-dialog-close>{{ 'button.close' | translate }}</button>
  </mat-dialog-actions>
}
